import React, { ReactNode, useState } from "react";
import {
  getColumns,
  getSelectedWithoutHeader,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { generatePath, infiniteNestLink, Link } from "polyrouter";
import { needs, useGuard } from "rbac";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { handleNullFields } from "utilities/stringUtils";
import { format } from "date-fns";
import CreateDeviceGroupModal from "../Create/CreateDeviceGroupModal";
import DeleteDeviceGroupsModal from "../Delete/DeleteDeviceGroupsModal";
import { GroupDto } from "generated/graphql";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { SortState } from "store/filters";
import { useDeviceGroups } from "../DeviceGroupsUtils";
import { NoResultsIndicator } from "elements";

type DeviceGroup = Pick<GroupDto, "id" | "name" | "createdAt"> & {
  deviceCount: number;
  modelCount: number;
};

type DeviceGroupRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
} & DeviceGroup;

export const DeviceGroups: React.FC = () => {
  const { t } = useTranslation();
  const { NAME: TABLE_NAME, COLUMNS } = TABLES.DEVICE_GROUPS;
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [sort, setSort] = useState({
    field: COLUMNS.CREATED_AT,
    direction: "DESC"
  } as SortState);
  const [searchTerm, setSearch] = useState<string | undefined>(undefined);
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const { data, fetching } = useDeviceGroups();
  const deviceGroups: Array<DeviceGroup> =
    data
      ?.filter(
        group =>
          !searchTerm ||
          group.name
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase())
      )
      .map(group => {
        const modelCount =
          group.devices?.reduce(
            (models, { hardwareModel }) => models.add(hardwareModel ?? ""),
            new Set<string>()
          ).size ?? 0;
        return {
          ...group,
          deviceCount: group.devices?.length ?? 0,
          modelCount
        };
      }) ?? [];

  sortTableData(deviceGroups, sort);

  const toggleItem = toggleItemWithSet(
    deviceGroups.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);
  const selectedGroups = getSelectedWithoutHeader(selectedItems);
  const anySelected = selectedGroups.length > 0;

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_GROUP_NAME"),
    [COLUMNS.DEVICE_COUNT]: t("DEVICE_COUNT"),
    [COLUMNS.MODEL_COUNT]: t("DEVICE_MODEL_other"),
    [COLUMNS.CREATED_AT]: t("DATE_CREATED")
  };
  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const editRequirement = "UPDATE_SITES";
  const userCanEdit = useGuard(needs(editRequirement));
  // there might be one additional column besides those user choose to display (checkbox to select rows)
  const columnCount = shown.length + (userCanEdit ? 1 : 0);

  const { Content } = Layout;
  return (
    <Layout>
      <Content className="no-sidebar">
        <div className="table-heading">
          <span className="metric" data-testid="group-count">
            {deviceGroups.length}
          </span>
          <span className="title ml-5">
            {t("DEVICE_GROUP", { count: deviceGroups.length })}
          </span>
        </div>
        <div className="grouping mb-15 table-actions">
          <ProtectedLink
            to={infiniteNestLink("/add")}
            requirement={editRequirement}
            className="btn btn-primary"
          >
            {t("ADD")}
          </ProtectedLink>
          <ProtectedLink
            disabled={!anySelected}
            to={anySelected ? infiniteNestLink("/remove") : ""}
            requirement={editRequirement}
            className={`btn ${anySelected ? "" : "disabled"}`}
          >
            {t("DELETE")}
          </ProtectedLink>
          <TableSearchEdit
            search={{
              onSearch: setSearch,
              searchPlaceholder: `${t("SEARCH")} ${
                columnTranslations[COLUMNS.NAME]
              }`
            }}
            edit={{
              tableName: TABLE_NAME,
              shownColumns: shown,
              hiddenColumns: hidden
            }}
          />
        </div>
        <div className="table-container">
          <table className="table custom-table table-filterable">
            <TableBulkItemHeader
              showChecks={userCanEdit}
              checked={itemChecked(HEADER_ID)}
              onCheck={() =>
                toggleAll(
                  deviceGroups.map(group => group.id),
                  selectedItems,
                  setSelectedItems
                )
              }
            >
              {generateTableHeaders(shown, getTableHeaders)}
            </TableBulkItemHeader>
            <TableBody
              loading={fetching}
              cols={columnCount}
              accommodateScrollbar
            >
              {deviceGroups.map(group => (
                <DeviceGroupRow
                  {...group}
                  key={group.id}
                  onCheck={() => toggleItem(group.id)}
                  checked={itemChecked(group.id)}
                  showChecks={userCanEdit}
                  shownColumns={shown.map(column => column.key)}
                />
              ))}
            </TableBody>
          </table>
          {!fetching && !deviceGroups.length ? (
            <NoResultsIndicator title={t("NO_DEVICE_GROUPS")} />
          ) : null}
        </div>
        <ProtectedRoute nest path="/add" requirement={editRequirement}>
          <CreateDeviceGroupModal />
        </ProtectedRoute>
        <ProtectedRoute nest path="/remove" requirement={editRequirement}>
          <DeleteDeviceGroupsModal
            groups={selectedGroups}
            onSuccess={() => {
              setSelectedItems([]);
            }}
          />
        </ProtectedRoute>
      </Content>
    </Layout>
  );
};

export const DeviceGroupRow: React.FC<DeviceGroupRowProps> = ({
  id,
  name,
  createdAt,
  deviceCount,
  modelCount,
  checked,
  showChecks,
  onCheck,
  shownColumns
}) => {
  const { COLUMNS } = TABLES.DEVICE_GROUPS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link nest to={generatePath("/details/:id", { id })}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.DEVICE_COUNT:
        content = deviceCount;
        break;
      case COLUMNS.MODEL_COUNT:
        content = modelCount;
        break;
      case COLUMNS.CREATED_AT:
        content = createdAt
          ? format(new Date(createdAt), "M/d/yyyy")
          : handleNullFields(createdAt);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(id)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
